import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Typography,
  Divider,
  Select,
  MenuItem,
  TextField,
  FormControl,
  IconButton,
  CircularProgress,
  Tooltip,
  Button,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { fieldStyles } from "services/utils";

const TrainingCard = ({
  row,
  index,
  t,
  empId,
  empTrainingOptions,
  trainingCompMap,
  jobLvls,
  empCompGaps,
  isEditing,
  savingRowIndex,
  selectedLevelIds,
  toggleEditing,
  handleSaveTrainingRow,
  handleTrainingFieldChange,
  handleAchievedLevelChange,
  setRowToDelete,
  setConfOpen,
  isMyTrainingsView = false,
}) => {
  console.log("Row::", row);
  // --- Data Parsing & Logic ---
  const now = new Date();
  const startDate = new Date(row[2]);
  const endDate = new Date(row[3]);

  const isPast = startDate < now && endDate < now;
  const isOngoing = startDate < now && endDate >= now;
  const isFuture = startDate > now && endDate > now;

  const empJobId = empCompGaps?.[row[5]]?.jobId;

  //---Logic for selectedLevelId fallback ---
  let selectedLevelId = row[4]; // ideal case (new row with ID)
  if (!selectedLevelId) {
    const compId = trainingCompMap[row[0]];
    const level = (jobLvls[empJobId] || []).find(
      (lvl) => lvl.name === row[6] && lvl.value === row[7] && lvl.competencyId === compId
    );
    selectedLevelId = level?.id || "";
  }

  const total = endDate - startDate;
  const passed = now - startDate;
  const progress = Math.min(100, Math.max(0, (passed / total) * 100));

  // Determine progress color based on 3 categories
  let progressColor = "#3b82f6"; // default Blue
  if (progress <= 0) {
    progressColor = "#d1d5db"; // Gray for Not Started
  } else if (progress < 50) {
    progressColor = "#3b82f6"; // Blue for low
  } else if (progress < 80) {
    progressColor = "#facc15"; // Yellow for medium
  } else {
    progressColor = "#16a34a"; // Green for high / completed
  }

  // Text inside progress
  let progressText = "";
  if (progress <= 0) {
    progressText = t("user.trainings.notStarted");
  } else if (progress < 100) {
    progressText = `${Math.round(progress)}% ${t("user.trainings.inProgress")}`;
  } else {
    progressText = t("user.trainings.completed");
  }

  const isBehind = progress < 80;

  // KPI Mapping
  const kpis = row[14];
  const weights = row[15];
  const trends = row[17];
  const kpiList = kpis ? kpis.split(",").map((k) => k.trim()) : [];
  const weightList = weights ? weights.split(",").map((w) => w.trim()) : [];
  const trendList = trends ? trends.split(",").map((t) => t.trim()) : [];

  const kpiInsights = kpiList.map((kpi, i) => ({
    name: kpi,
    weight: weightList[i] ?? "—",
    trend: trendList[i] ?? "0.00",
  }));

  return (
    <Paper
      sx={{
        p: 2,
        pb: 0,
        borderRadius: "4px",
        border: "1px solid #e5e7eb",
        boxShadow: "none",
      }}
    >
      {/* 1. Progress Bar */}
      <Box
        width="100%"
        height={18}
        borderRadius={6}
        overflow="hidden"
        position="relative"
        display="flex"
      >
        <Box
          width={`${progress}%`}
          bgcolor={progressColor}
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent={progress > 0 ? "center" : undefined}
          sx={{ transition: "width 0.3s ease" }}
        >
          {/* Text only inside completed part for In Progress / Completed */}
          {progress > 0 && (
            <Typography
              variant="caption"
              sx={{ fontWeight: "bold", color: "#000000", whiteSpace: "nowrap" }}
            >
              <Box
                component="span"
                sx={{ display: { xs: progress < 100 ? "inline" : "none", sm: "none" } }}
              >
                {progress < 100 ? `${Math.round(progress)}%` : ""}
              </Box>
              <Box
                component="span"
                sx={{ display: { xs: progress < 100 ? "none" : "inline", sm: "inline" } }}
              >
                {progressText}
              </Box>
            </Typography>
          )}
        </Box>
        {/* Remaining Part */}
        <Box flex={1} bgcolor="#f0f0f0" height="100%" />

        {progress <= 0 && (
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              width: "100%",
              textAlign: "center",
              top: "50%",
              transform: "translateY(-50%)",
              fontWeight: "bold",
              color: "#000000",
              pointerEvents: "none",
            }}
          >
            {progressText}
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 1, height: 2, backgroundColor: "#9ca3af" }} />

      {/* 2. Title / Editable Name */}
      {isFuture && isEditing ? (
        <Select
          size="small"
          value={row[0]} // trainingId (e.g., 101)
          onChange={(e) => handleTrainingFieldChange(e, index, empId, 0)}
          fullWidth
          sx={fieldStyles}
          displayEmpty // Allows showing the renderValue when row[0] is empty
          renderValue={(selectedId) => {
            //Logic to find the Name based on the ID
            // const training = empTrainingOptions?.find((t) => String(t.id) === String(selectedId));
            // return training?.name || t("user.trainings.selectTraining");

            // 1. Prioritize row[1] (the name already in your data)
            // 2. Fallback to searching empTrainingOptions (for brand-new rows)
            // 3. Final fallback to "Select Training" placeholder
            const displayName =
              row[1] || empTrainingOptions?.find((t) => String(t.id) === String(selectedId))?.name;

            return displayName || t("user.trainings.selectTraining");
          }}
        >
          {empTrainingOptions?.map((training) => (
            <MenuItem key={training.id} value={training.id}>
              {training.name}
            </MenuItem>
          ))}
        </Select>
      ) : (
        <Typography variant="body2" fontWeight="bold" color="#344767" mb={1}>
          {/* Static display version */}
          {/* {empTrainingOptions?.find((t) => String(t.id) === String(row[0]))?.name || "—"} */}

          {/* {isMyTrainingsView
            ? row[1]
            : empTrainingOptions?.find((t) => String(t.id) === String(row[0]))?.name || "—"} */}
          {row[1] || empTrainingOptions?.find((t) => String(t.id) === String(row[0]))?.name || "—"}
        </Typography>
      )}

      {/* 3. Details (Dates and Levels) */}
      <Box display="flex" flexDirection="column" gap={0.5} mt={0.5} ml={1}>
        {/* Start Date */}
        <Box display="flex" alignItems="center" gap={1}>
          <Typography sx={{ width: 80, fontWeight: "bold", fontSize: 13 }}>
            {t("user.trainings.startDate")}:
          </Typography>
          {isFuture && isEditing ? (
            <TextField
              type="date"
              size="small"
              value={row[2].slice(0, 10)}
              onChange={(e) => handleTrainingFieldChange(e, index, empId, 2)}
              fullWidth
              sx={fieldStyles}
            />
          ) : (
            <Typography sx={{ width: 80, fontSize: 13 }}>
              {startDate.toLocaleDateString()}
            </Typography>
          )}
        </Box>

        {/* End Date */}
        <Box display="flex" alignItems="center" gap={1}>
          <Typography sx={{ width: 80, fontWeight: "bold", fontSize: 13 }}>
            {t("user.trainings.endDate")}:
          </Typography>
          {isFuture && isEditing ? (
            <TextField
              type="date"
              size="small"
              value={row[3].slice(0, 10)}
              onChange={(e) => handleTrainingFieldChange(e, index, empId, 3)}
              fullWidth
              sx={fieldStyles}
            />
          ) : (
            <Typography sx={{ width: 80, fontSize: 13 }}>{endDate.toLocaleDateString()}</Typography>
          )}
        </Box>

        {/* Expected Outcome */}
        <Box display="flex" alignItems="center" gap={1}>
          <Typography sx={{ width: 160, fontWeight: "bold", fontSize: 13 }}>
            {t("user.trainings.expectedOutcome")}:
          </Typography>
          {(isFuture || isOngoing) && isEditing ? (
            <TextField
              size="small"
              select
              value={selectedLevelIds[index] || ""}
              onChange={(e) => handleTrainingFieldChange(e, index, empId, 9)}
              sx={{
                minWidth: 180,
                "& .MuiInputBase-root": { height: 40 },
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                },
                ...fieldStyles,
              }}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selectedId) => {
                  const compId = trainingCompMap[row[0]];
                  const selected = jobLvls.find(
                    (lvl) => String(lvl.id) === String(selectedId) && lvl.competencyId === compId
                  );
                  return selected
                    ? `${selected.name} (${selected.value})`
                    : t("user.trainings.selectLevel");
                },
              }}
            >
              {jobLvls
                .filter((lvl) => lvl.competencyId === trainingCompMap[row[0]])
                .map((lvl) => (
                  <MenuItem key={lvl.id} value={lvl.id}>
                    {`${lvl.name} (${lvl.value})`}
                  </MenuItem>
                ))}
            </TextField>
          ) : (
            <Typography sx={{ width: 160, fontSize: 13 }}>
              {row[6] && row[7] ? `${row[6]} (${row[7]})` : "—"}
            </Typography>
          )}
        </Box>

        {/* Achieved Level */}
        <Box display="flex" alignItems="center" gap={1}>
          <Typography sx={{ width: 180, fontWeight: "bold", fontSize: 13 }}>
            {t("user.trainings.achievedLevel")}:
          </Typography>
          {isMyTrainingsView ? (
            <Typography sx={{ width: 80, fontSize: 13 }}>
              {row[10] && row[11] ? `${row[10]} (${row[11]})` : "—"}
            </Typography>
          ) : isPast ? (
            <FormControl fullWidth>
              <Select
                size="small"
                value={row[9] || ""} // achievedLevelId
                onChange={(e) => handleAchievedLevelChange(e, index, empId)}
                displayEmpty
                sx={{
                  minWidth: 140,
                  maxWidth: "100%",
                  "& .MuiInputBase-root": {
                    height: 28, // realistic small height
                    minHeight: 0, // override MUI default
                    paddingTop: 0,
                    paddingBottom: 0,
                    fontSize: 12, // optional smaller text
                  },
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    height: "100%",
                    paddingTop: 0,
                    paddingBottom: 0,
                    whiteSpace: "nowrap",
                  },
                  ...fieldStyles,
                }}
                renderValue={(selectedId) => {
                  const compId = trainingCompMap[row[0]]; // Map training → competency
                  const selected = jobLvls.find(
                    (lvl) => lvl.id === selectedId && lvl.competencyId === compId
                  );
                  return selected
                    ? `${selected.name} (${selected.value})`
                    : t("admin.compToJob.selectLevel");
                }}
                MenuProps={{
                  disablePortal: true,
                  PaperProps: {
                    style: { maxHeight: 200, overflowY: "auto", zIndex: 1300 },
                  },
                }}
              >
                {jobLvls
                  .filter((lvl) => lvl.competencyId === trainingCompMap[row[0]])
                  .map((lvl) => (
                    <MenuItem key={lvl.id} value={lvl.id}>
                      {`${lvl.name} (${lvl.value})`}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          ) : (
            <Typography sx={{ width: 80, fontSize: 13 }}>
              {row[10] && row[11] ? `${row[10]} (${row[11]})` : "—"}
            </Typography>
          )}
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <Typography sx={{ width: 25, fontWeight: "bold", fontSize: 13 }}>
            {t("user.trainings.gap")}:
          </Typography>
          <Typography sx={{ width: 25, fontSize: 13 }}>
            {row[11] === null || row[11] === undefined || row[11] === 0 ? "—" : row[11] - row[7]}
          </Typography>
        </Box>
      </Box>

      {/* 4. KPI Section */}
      {kpiInsights.length > 0 && (
        <Box sx={{ mt: 1, ml: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
            {isBehind ? t("user.training.behind") : t("user.training.onTrack")}
          </Typography>
          {kpiInsights.map((kpi, i) => {
            const trendVal = parseFloat(kpi.trend);
            const hasTrend =
              kpi.trend &&
              kpi.trend.toLowerCase() !== "nu" &&
              !Number.isNaN(trendVal) &&
              trendVal !== 0;
            const trendDisplay = hasTrend
              ? trendVal > 0
                ? `+${kpi.trend}%`
                : `${kpi.trend}%`
              : "-";
            return (
              <Typography key={i} sx={{ fontSize: 12, color: "text.secondary" }}>
                {t("user.training.kpiLine", { name: kpi.name, weight: kpi.weight })}{" "}
                {t("user.training.trend", { value: trendDisplay })}
              </Typography>
            );
          })}
        </Box>
      )}

      {/* 5. Actions */}
      {!isMyTrainingsView && (
        <Box
          display="flex"
          justifyContent="flex-end"
          gap={1}
          mt={0} // remove extra top margin
          mb={0} // remove any bottom margin
          sx={{
            alignSelf: "flex-end", // stick to bottom of its content, no stretch
          }}
        >
          {(isFuture || isOngoing) &&
            (isEditing ? (
              savingRowIndex === index ? (
                <CircularProgress size={10} />
              ) : (
                <Button size="small" onClick={() => handleSaveTrainingRow(index)}>
                  {t("user.trainings.save")}
                </Button>
              )
            ) : (
              <IconButton size="small" onClick={() => toggleEditing(index)}>
                <EditIcon fontSize="small" />
              </IconButton>
            ))}
          <IconButton
            size="small"
            color="error"
            onClick={() => {
              setRowToDelete({ id: row[8], index });
              setConfOpen(true);
            }}
            sx={{
              padding: 2, // smaller than default
              minWidth: 0,
              minHeight: 0,
              width: 24,
              height: 24,
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Paper>
  );
};

TrainingCard.propTypes = {
  row: PropTypes.array.isRequired,
  index: PropTypes.number.isRequired,
  t: PropTypes.func.isRequired,
  empId: PropTypes.any,
  empTrainingOptions: PropTypes.array,
  trainingCompMap: PropTypes.object,
  jobLvls: PropTypes.array,
  empCompGaps: PropTypes.object,
  isEditing: PropTypes.bool,
  savingRowIndex: PropTypes.number,
  selectedLevelIds: PropTypes.object,
  toggleEditing: PropTypes.func.isRequired,
  handleSaveTrainingRow: PropTypes.func.isRequired,
  handleTrainingFieldChange: PropTypes.func.isRequired,
  handleAchievedLevelChange: PropTypes.func.isRequired,
  setRowToDelete: PropTypes.func.isRequired,
  setConfOpen: PropTypes.func.isRequired,
  isMyTrainingsView: PropTypes.bool,
};

export default TrainingCard;
